import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Mail, Lock, Music, ArrowLeft, AlertCircle, Loader } from 'lucide-react';
import api from '../services/api';
import { getSafeRedirectPath } from '../utils/redirect';

const Login = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { login, loading, error, clearError, isAuthenticated } = useAuth();

    // Form state
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const [localLoading, setLocalLoading] = useState(false);
    const [siteLogo, setSiteLogo] = useState(null);

    // Clear errors when component mounts
    useEffect(() => {
        clearError();
    }, [clearError]);

    // Redirect if already authenticated
    useEffect(() => {
        if (isAuthenticated) {
            navigate(getSafeRedirectPath(searchParams));
        }
    }, [isAuthenticated, navigate, searchParams]);

    // Fetch site logo
    useEffect(() => {
        const fetchSiteLogo = async () => {
            try {
                const response = await api.get('/admin/settings/public');
                if (response.data.success) {
                    const logoUrl = response.data.data.site_logo;
                    if (logoUrl) {
                        setSiteLogo(logoUrl);
                    }
                }
            } catch (error) {
                console.error('Error fetching site logo:', error);
            }
        };

        fetchSiteLogo();
    }, []);

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLocalLoading(true);
        clearError();

        // Basic validation
        if (!formData.email || !formData.password) {
            setLocalLoading(false);
            return;
        }

        try {
            const result = await login({
                email: formData.email.trim(),
                password: formData.password
            });

            if (result.success) {
                navigate(getSafeRedirectPath(searchParams));
            }
        } catch (error) {
            console.error('Login error:', error);
        } finally {
            setLocalLoading(false);
        }
    };

    const isFormLoading = loading || localLoading;

    return (
        <div className="min-h-screen bg-hero flex items-center justify-center px-6 py-12">
            {/* Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse-slow"></div>
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse-slow delay-200"></div>
            </div>

            <div className="relative z-10 w-full max-w-md">
                {/* Header */}
                <div className="text-center mb-8">
                    <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors duration-300 mb-6">
                        <ArrowLeft className="w-5 h-5" />
                        Back to Home
                    </Link>
                    
                    {siteLogo ? (
                        <div className="mx-auto mb-4">
                            <img
                                src={siteLogo}
                                alt="The Deep Wave Logo"
                                width="180"
                                className="object-cover rounded-lg mx-auto"
                                style={{
                                    maxWidth: '180px',
                                    width: '180px',
                                    minWidth: 'auto'
                                }}
                            />
                        </div>
                    ) : (
                        <div className="text-6xl mb-4">🌊</div>
                    )}
                    <h1 className="text-4xl font-black gradient-text mb-2">
                        Welcome Back
                    </h1>
                    <p className="text-gray-400">
                        Sign in to continue your musical journey
                    </p>
                </div>

                {/* Login Form */}
                <div className="glass-card p-8">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Error Message */}
                        {error && (
                            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-center space-x-3">
                                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                                <p className="text-red-400 text-sm">{error}</p>
                            </div>
                        )}

                        {/* Email Field */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-semibold text-gray-300 mb-2">
                                Email
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="input-field pl-12"
                                    placeholder="your@email.com"
                                    required
                                    disabled={isFormLoading}
                                />
                            </div>
                        </div>

                        {/* Password Field */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-semibold text-gray-300 mb-2">
                                Password
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    className="input-field pl-12 pr-12"
                                    placeholder="Enter your password"
                                    required
                                    disabled={isFormLoading}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors duration-300"
                                    disabled={isFormLoading}
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        {/* Forgot Password Link */}
                        <div className="text-right">
                            <Link 
                                to="/forgot-password" 
                                className="text-sm text-purple-400 hover:text-purple-300 transition-colors duration-300"
                            >
                                Forgot your password?
                            </Link>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isFormLoading || !formData.email || !formData.password}
                            className="w-full btn-primary flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isFormLoading ? (
                                <>
                                    <Loader className="w-5 h-5 animate-spin" />
                                    Signing In...
                                </>
                            ) : (
                                <>
                                    <Music className="w-5 h-5" />
                                    Sign In
                                </>
                            )}
                        </button>
                    </form>

                    {/* Divider */}
                    <div className="my-8 flex items-center">
                        <div className="flex-1 border-t border-white/10"></div>
                        <span className="px-4 text-gray-500 text-sm">or</span>
                        <div className="flex-1 border-t border-white/10"></div>
                    </div>

                    {/* Register Link */}
                    <div className="text-center">
                        <p className="text-gray-400 mb-4">
                            Don't have an account yet?
                        </p>
                        <Link 
                            to="/register" 
                            className="btn-secondary w-full flex items-center justify-center gap-3"
                        >
                            Create Account
                        </Link>
                    </div>
                </div>

                {/* Additional Links */}
                <div className="text-center mt-8 text-sm text-gray-500">
                    <div className="flex justify-center space-x-6">
                        <Link to="/privacy" className="hover:text-purple-400 transition-colors duration-300">
                            Privacy Policy
                        </Link>
                        <Link to="/terms" className="hover:text-purple-400 transition-colors duration-300">
                            Terms of Service
                        </Link>
                        <Link to="/support" className="hover:text-purple-400 transition-colors duration-300">
                            Support
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;